"use client";

import { ArrowDownRight, ArrowRight, ArrowUpRight, Clock, Minus } from "lucide-react";
import type {
	MarketTrend,
	MarketsBoardComponent,
	MarketsExchangeContent,
	MarketsInflationContent,
	MarketsInterestContent,
} from "../../types";
import { RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import { MarketsSparkline } from "./MarketsSparkline";

/** The projector bakes the featured deltas with a French period suffix
 * ("+0,38 % 1jr") — the number is data but the suffix is UI text. */
function localizeDeltaSuffix(value: string, locale: string): string {
	return value
		.replace(/\b1jr\b/, t(locale, "deltaOneDaySuffix"))
		.replace(/\b2jrs\b/, t(locale, "deltaTwoDaysSuffix"));
}

/**
 * Markets board: three "key markets" cards for the home page.
 *
 * Designed to read as a calm, scannable strip rather than a dashboard. The
 * earlier version felt overloaded — colored left edges, ringed cards on a
 * tinted background, two-column currency lists, sparklines on every card —
 * so this revision strips the chrome and lets the data carry the visual
 * weight: bare cards, single-column lists, one sparkline only on the
 * featured exchange pair.
 *
 * The three sub-cards remain optional in the payload; authors can render any
 * subset. On mobile they stack one per row; from `lg` they sit 6/3/3.
 */
export function MarketsBoardRenderer({ component }: { component: MarketsBoardComponent }) {
	const { title, subtitle, actionLabel, actionHref, exchange, inflation, interest } = component.content;
	const resolvedAction = resolveLinkHref(actionHref);

	return (
		<section className="mx-[calc(50%-50vw)] bg-[#d0ebf7] pt-10 pb-14">
			<div className="relative mx-auto max-w-7xl px-6 md:px-8">
				{title ? (
					<header className="mb-6 md:mb-8">
						<h3 className="text-[28px] font-bold tracking-tight text-[#00B862] md:text-[32px]">
							{title}
						</h3>
						<div className="mt-2 h-1 w-12 rounded-sm bg-[#FFC105]" aria-hidden="true" />
						{subtitle ? (
							<p className="mt-2 max-w-2xl text-[16px] xl:text-[18px] leading-relaxed text-slate-600">
								{subtitle}
							</p>
						) : null}
					</header>
				) : null}
				{/* Layout:
				    - mobile  : single column, stacked
				    - md (≥768): exchange full-width, then inflation + interest 50/50 below
				    - lg (≥1024): three equal columns (1/3 each)
				    Avoids shrinking the cards prematurely on tablets. */}
				<div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-6">
					{exchange ? (
						<div className="md:col-span-2 lg:col-span-1">
							<ExchangeCard
								data={exchange}
								actionLabel={actionLabel}
								actionHref={resolvedAction}
							/>
						</div>
					) : null}
					{inflation ? (
						<div className="md:col-span-1 lg:col-span-1">
							<InflationCard data={inflation} />
						</div>
					) : null}
					{interest ? (
						<div className="md:col-span-1 lg:col-span-1">
							<InterestCard data={interest} />
						</div>
					) : null}
				</div>
			</div>
		</section>
	);
}

/* -------------------------------------------------------------------------- */
/* Card chrome                                                                */
/* -------------------------------------------------------------------------- */

/** Shared card. Keeps a thin shadow + ring so cards still feel like surfaces
 *  on the tinted section background, but drops the colored accent stripe and
 *  the meta "tag" pills used in the previous iteration. */
function MarketCard({
	children,
	footer,
}: {
	children: React.ReactNode;
	footer?: React.ReactNode;
}) {
	return (
		<div className="flex h-full flex-col overflow-hidden rounded-md bg-white shadow-[var(--shadow-e1)] ring-1 ring-slate-200/80">
			<div className="flex-1 px-5 pt-4 pb-3 lg:px-7 lg:pt-6 lg:pb-5">{children}</div>
			{footer ? (
				<div className="border-t border-slate-100 px-5 py-2.5 lg:px-7 lg:py-4">{footer}</div>
			) : null}
		</div>
	);
}

/** Card heading + small meta line. Keeps the same hierarchy across cards
 *  so the eye lands on the title first, then the date, then the data. */
function CardHeading({ title, asOf }: { title: string; asOf?: string }) {
	return (
		<div className="mb-3 flex items-baseline justify-between gap-2">
			<h4 className="text-[22px] md:text-[24px] xl:text-[26px] font-bold leading-tight text-[#00B862]">
				{title}
			</h4>
			{asOf ? <p className="text-[11px] xl:text-[13px] text-slate-500">{asOf}</p> : null}
		</div>
	);
}

function MoreLink({ label, href }: { label: string; href: string }) {
	return (
		<RuntimeLink
			href={href}
			className="group inline-flex items-center gap-1.5 text-[14px] md:text-[15px] lg:text-[16px] font-semibold text-[#00A356] transition hover:text-[#00B862]"
		>
			{label}
			<ArrowRight className="h-3.5 w-3.5 text-[#FFC105] transition group-hover:translate-x-0.5" />
		</RuntimeLink>
	);
}

function TrendArrow({ trend, className = "h-3.5 w-3.5" }: { trend?: MarketTrend; className?: string }) {
	const locale = useLocale();
	if (trend === "up") return <ArrowUpRight className={`${className} text-[#15B07A]`} aria-label={t(locale, "trendUp")} />;
	if (trend === "down") return <ArrowDownRight className={`${className} text-[#D93B3B]`} aria-label={t(locale, "trendDown")} />;
	return <Minus className={`${className} text-slate-300`} aria-label="stable" />;
}

/* -------------------------------------------------------------------------- */
/* Exchange rates card                                                        */
/* -------------------------------------------------------------------------- */

function ExchangeCard({
	data,
	actionLabel,
	actionHref,
}: {
	data: MarketsExchangeContent;
	actionLabel?: string;
	actionHref?: string;
}) {
	const locale = useLocale();
	const featured = data.featured;
	// Tinted chip for the delta — clearer than colored text on white because
	// it groups the arrow + value into a single visual unit and lifts off the
	// surrounding card. Up = soft green, down = soft red, flat = neutral.
	const trendChip =
		featured?.trend === "up"
			? "bg-[#E7F8F1] text-[#15805B]"
			: featured?.trend === "down"
				? "bg-[#FDECEC] text-[#B7282B]"
				: "bg-slate-100 text-slate-600";
	const moreHref = resolveLinkHref(data.moreHref);
	const parallelHref = resolveLinkHref(data.parallelHref);
	const hasParallelLink = !!(parallelHref && data.parallelLabel);
	const hasMoreLink = !!(moreHref && data.moreLabel);
	// `rows` is injected by the deploy-time projector (fragment-resolved on the public
	// site). The admin preview renders the unresolved authoring shape, so rows can be
	// missing — default to [] and show a placeholder instead of crashing on `.map`.
	const rows = data.rows ?? [];

	return (
		<MarketCard
			footer={
				// Each link renders independently — earlier the whole footer
				// hinged on `moreHref && moreLabel`, so dropping "Plus de
				// détails" also silently hid "Cours parallèle". The two CTAs
				// answer different questions ("see another rate type" vs
				// "see the full table") and authors should be free to keep
				// either one alone.
				hasParallelLink || hasMoreLink ? (
					<div className="flex flex-wrap items-center justify-between gap-3">
						{hasParallelLink ? (
							<MoreLink label={data.parallelLabel as string} href={parallelHref as string} />
						) : (
							<span />
						)}
						{hasMoreLink ? <MoreLink label={data.moreLabel as string} href={moreHref as string} /> : null}
					</div>
				) : null
			}
		>
			{/* Date intentionally omitted from the heading — it lives next to
			    "cours indicatif" inside the featured panel below to read as
			    one piece of meta. */}
			<CardHeading title={data.title ?? t(locale, "exchangeRates")} />

			{/* Body layout shifts at md only:
			    - mobile : single column (featured + sparkline-below + currency list)
			    - md     : 2-col grid — text on the left, full-height chart on the right
			    - lg+    : back to single column with compact sparkline next to featured
			    The card spans the full width at md (md:col-span-2 above) so the
			    50/50 split actually has room; at lg the card is half-width so the
			    big chart no longer makes sense. */}
			<div className="md:grid md:grid-cols-2 md:items-stretch md:gap-6 lg:block">
				<div>
					{featured ? (
						<div className="mb-4">
							{/* Rate kept smaller than the card title so the title stays
							    the primary anchor of the card. */}
							<div className="text-[16px] xl:text-[18px] font-bold leading-tight text-[#00B862]">
								{featured.label}
							</div>

							{/* Trend chips on their own row so they don't collide with
							    the rate at narrow widths. delta1d is the primary movement;
							    delta2d (if present) sits next to it as a subdued companion. */}
							{(featured.delta1d || featured.delta2d) ? (
								<div className="mt-2 flex flex-wrap items-center gap-2">
									{featured.delta1d ? (
										<span
											className={`inline-flex items-center gap-1 whitespace-nowrap rounded-sm px-2 py-1 text-[11px] xl:text-[13px] font-semibold ${trendChip}`}
										>
											<TrendArrow trend={featured.trend} className="h-3 w-3" />
											{localizeDeltaSuffix(featured.delta1d, locale)}
										</span>
									) : null}
									{featured.delta2d ? (
										<span className="inline-flex items-center whitespace-nowrap rounded-sm bg-slate-100 px-2 py-1 text-[11px] xl:text-[13px] font-medium text-slate-600">
											{localizeDeltaSuffix(featured.delta2d, locale)}
										</span>
									) : null}
								</div>
							) : null}

							{/* Caption row pulls in the card-level date too: clock + date/
							    time on the left, "cours indicatif" italicised on the right.
							    Falls back to whichever of the two is set. */}
							{(data.asOf || featured.timestamp || featured.caption) ? (
								<div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] xl:text-[13px] text-slate-500">
									{data.asOf || featured.timestamp ? (
										<span className="inline-flex items-center gap-1.5">
											<Clock className="h-3 w-3" aria-hidden="true" />
											{data.asOf ?? featured.timestamp}
										</span>
									) : (
										<span />
									)}
									{featured.caption ? (
										<span className="italic text-slate-400">{featured.caption}</span>
									) : null}
								</div>
							) : null}

							{/* Mobile-only labelled chart. */}
							{data.sparkline ? (
								<MarketsSparkline
									values={data.sparkline}
									labels={data.sparklineLabels}
									className="mt-3 block h-44 w-full md:hidden"
								/>
							) : null}
						</div>
					) : null}

					{rows.length === 0 ? (
						<div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[12px] italic text-slate-500">
							Données dynamiques — les valeurs s'affichent une fois la page déployée.
						</div>
					) : (
					<ul className="divide-y divide-slate-100">
						{rows.map((r) => (
							<li key={r.code} className="flex items-center justify-between py-1.5 text-[14px] md:text-[15px] lg:text-[16px]">
								<span className="inline-flex items-center gap-2.5 font-semibold text-slate-700">
									{r.flag ? (
										<span
											className={`fi fi-${r.flag} text-[14px] xl:text-[16px]`}
											style={{ display: "inline-flex", boxShadow: "0px 1px 2px rgba(0,0,0,0.18)" }}
										/>
									) : null}
									{r.code}
								</span>
								<span className="inline-flex items-center gap-2 whitespace-nowrap text-slate-700">
									<span className="tabular-nums">{r.value}</span>
									<span className="text-[11px] xl:text-[13px] text-slate-400">CDF</span>
									<TrendArrow trend={r.trend} />
								</span>
							</li>
						))}
					</ul>
					)}
					{actionHref && actionLabel ? (
						<div className="mt-4 flex justify-center">
							<RuntimeLink
								href={actionHref}
								className="inline-flex items-center rounded-sm bg-[#FFC105] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-white shadow-sm transition hover:bg-[#E0A800]"
							>
								{actionLabel}
							</RuntimeLink>
						</div>
					) : null}
				</div>

				{/* Big chart — only visible at md (when card spans the full width). */}
				{data.sparkline ? (
					<div className="hidden md:flex md:items-stretch lg:hidden">
						<MarketsSparkline
							values={data.sparkline}
							labels={data.sparklineLabels}
							className="block h-full min-h-[200px] w-full"
						/>
					</div>
				) : null}
			</div>
		</MarketCard>
	);
}

/* -------------------------------------------------------------------------- */
/* Inflation card                                                             */
/* -------------------------------------------------------------------------- */

function InflationCard({ data }: { data: MarketsInflationContent }) {
	const locale = useLocale();
	const moreHref = resolveLinkHref(data.moreHref);
	const asOf = [data.asOf, data.source].filter(Boolean).join(" · ");
	// `rows` is normally injected by the deploy-time projector. The admin
	// preview renders the unresolved authoring shape, so rows can be missing
	// when the block is configured in dynamic mode.
	const rows = data.rows ?? [];
	const isDynamicUnresolved = data.mode === "dynamic" && rows.length === 0;
	return (
		<MarketCard
			footer={moreHref && data.moreLabel ? <MoreLink label={data.moreLabel} href={moreHref} /> : null}
		>
			<CardHeading title={data.title ?? t(locale, "inflationRate")} />
			{/* Unit caption + date share one row — caption left, date right. */}
			{(data.unitCaption || asOf) ? (
				<div className="mb-2 flex items-baseline justify-between gap-3 text-[11px] xl:text-[13px] text-slate-500">
					{data.unitCaption ? (
						<span className="italic text-slate-400">{data.unitCaption}</span>
					) : (
						<span />
					)}
					{asOf ? <span>{asOf}</span> : null}
				</div>
			) : null}
			{isDynamicUnresolved ? (
				<div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[12px] italic text-slate-500">
					Données dynamiques — les valeurs s'affichent une fois la page déployée
					(la rangée la plus récente du type <code>{data.dynamicComponent?.contentTypeKey ?? ""}</code> sera utilisée).
				</div>
			) : (
				<ul className="divide-y divide-slate-100">
					{rows.map((r) => (
						<li
							key={r.label}
							className="flex items-center justify-between gap-3 py-1.5 text-[14px] md:text-[15px] lg:text-[16px]"
						>
							<span className="text-slate-600">{r.label}</span>
							<span className="whitespace-nowrap font-bold tabular-nums text-[#00B862]">{r.value}</span>
						</li>
					))}
				</ul>
			)}
		</MarketCard>
	);
}

/* -------------------------------------------------------------------------- */
/* Interest rates card                                                        */
/* -------------------------------------------------------------------------- */

function InterestCard({ data }: { data: MarketsInterestContent }) {
	const locale = useLocale();
	const moreHref = resolveLinkHref(data.moreHref);
	return (
		<MarketCard
			footer={moreHref && data.moreLabel ? <MoreLink label={data.moreLabel} href={moreHref} /> : null}
		>
			<CardHeading title={data.title ?? t(locale, "interestRate")} />

			{data.directeur ? (
				<section className="mb-4">
					<div className="flex items-baseline justify-between gap-2 text-[14px] md:text-[15px] lg:text-[16px]">
						<h5 className="font-semibold text-slate-700">
							{data.directeur.title ?? t(locale, "policyRate")}
						</h5>
						{data.directeur.asOf ? (
							<span className="text-[11px] xl:text-[13px] text-slate-400">
								{(data.directeur.asOfLabel ?? t(locale, "asOfLabel"))} {data.directeur.asOf}
							</span>
						) : null}
					</div>
					<ul className="mt-1 divide-y divide-slate-100">
						{(data.directeur.rows ?? []).map((r) => (
							<li
								key={r.label}
								className="flex items-center justify-between gap-3 py-1 text-[14px] md:text-[15px] lg:text-[16px]"
							>
								<span className="text-slate-600">{r.label}</span>
								<span className="inline-flex items-center gap-1.5 whitespace-nowrap font-bold tabular-nums text-[#00B862]">
									{r.value}
									<TrendArrow trend={r.trend} />
								</span>
							</li>
						))}
					</ul>
				</section>
			) : null}

			{data.bons ? (
				<section>
					<div className="flex items-baseline justify-between gap-2 text-[14px] md:text-[15px] lg:text-[16px]">
						<h5 className="font-semibold text-slate-700">
							{data.bons.title ?? "Bons APA"}
						</h5>
						{data.bons.asOf ? (
							<span className="text-[11px] xl:text-[13px] text-slate-400">
								{(data.bons.asOfLabel ?? t(locale, "fromDate"))} {data.bons.asOf}
							</span>
						) : null}
					</div>
					<table className="mt-1 w-full border-collapse text-[12px] xl:text-[14px]">
						<thead>
							<tr className="text-left text-[10px] xl:text-[12px] uppercase tracking-wider text-slate-400">
								<th className="py-1 font-medium">{data.bons.columns?.jours ?? t(locale, "days")}</th>
								<th className="py-1 text-right font-medium">{data.bons.columns?.tmp ?? "TMP"}</th>
								<th className="py-1 text-right font-medium">{data.bons.columns?.tm ?? "Tm"}</th>
							</tr>
						</thead>
						<tbody>
							{(data.bons.rows ?? []).map((r) => (
								<tr key={r.jours} className="border-t border-slate-100">
									<td className="py-1.5 font-semibold text-slate-700 tabular-nums whitespace-nowrap">{r.jours}</td>
									<td className="py-1.5 text-right tabular-nums whitespace-nowrap text-[#00B862]">{r.tmp}</td>
									<td className="py-1.5 text-right tabular-nums whitespace-nowrap text-[#00B862]">{r.tm}</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>
			) : null}
		</MarketCard>
	);
}
